import React from "react";
import { Platform, View } from "react-native";
import { TFunction, useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  Panel,
  Heading,
  FlexGrid,
  theme,
  useResponsive,
  formatCurrency,
  Text,
  Icon,
} from "@clearabee/ui-library-native";
import { Input } from "components/Core/Input/Input";
import {
  FlatCatalogue,
  getFirstAvailableMonth,
  Modifier,
} from "@clearabee/ui-sdk";
import { styles, ids } from "./DateTimePanel.styles";
import { DeliveryOption, Timeslot } from "lib/types";
import { useData } from "lib/hooks/useData";
import { useFormContext } from "lib/hooks/useFormContext";
import { z } from "zod";
import { getValidationSchema } from "containers/ProductDetails/ProductDetails.validation";
import { showDeliveryText } from "lib/utils";
import { Tooltip } from "components/Tooltip/Tooltip";

export type RangeDeliveryOption = Required<Pick<DeliveryOption, "range">> &
  Omit<DeliveryOption, "range">;

interface DateTimePanelProps {
  deliveryOptions: FlatCatalogue["items"][0]["deliveryOptions"];
  blackouts: Modifier[];
  onListChange: (value: string) => void;
  selectedOption?: string;
  rangeDeliveryOptions?: RangeDeliveryOption;
  categorySku: string;
  isExchange?: boolean;
}

const mapDelivery =
  (
    deliveryOption: string | undefined,
    translate: TFunction,
    optionsLength: number,
  ) =>
  ({ title, charge }: DeliveryOption, index: number) => ({
    label: (
      <View
        style={[
          styles.deliveryOptionWrap,
          index === 0 && styles.deliveryOptionAccent,
          index !== 0 && styles.deliveryOptionBorder,
          index + 1 === optionsLength && {
            borderBottomWidth: 0,
          },
        ]}
      >
        <View style={styles.deliveryOption}>
          <Text
            fontSize="xsmall"
            styles={index === 0 && { color: theme.colors.light.base }}
          >
            {title.replace("timeslot", "time slot")}
          </Text>
          <Text
            styles={[
              { fontFamily: "ProximaSemiBold" },
              index === 0 && { color: theme.colors.light.base },
            ]}
          >
            {charge
              ? formatCurrency(charge)
              : translate("hwa.screens.productDetails.free")}
          </Text>
        </View>
        {!charge && (
          <Icon.Leaves
            color={`positive.${
              deliveryOption === title ? "light" : "lightest"
            }`}
          />
        )}
      </View>
    ),
    value: title,
  });

const mapRangeDelivery = ({ to, from }: Timeslot) => ({
  label: `${dayjs(`2020-01-01T${from}`).format("h:mm A")} - ${dayjs(
    `2020-01-01T${to}`,
  ).format("h:mm A")}`,
  value: `${from}-${to}`,
});

const collectionDateOffset = { from: 2, to: 14 };

export const DateTimePanel = ({
  deliveryOptions,
  blackouts,
  onListChange,
  selectedOption,
  rangeDeliveryOptions,
  categorySku,
  isExchange,
}: DateTimePanelProps): React.ReactElement => {
  const [{ serverDate }] = useData();
  const [translate] = useTranslation();
  const { screens } = useResponsive();
  const { Field, watch, setValue } =
    useFormContext<z.infer<ReturnType<typeof getValidationSchema>>>();
  const dateValue = watch("date");

  const showSkipHireCollectionDate = categorySku === "SKIPS";

  const collectionDateDisabledDays = dateValue
    ? [
        ...blackouts,
        {
          before: dayjs(dateValue)
            .add(collectionDateOffset.from, "day")
            .toDate(),
        },
        {
          after: dayjs(dateValue).add(collectionDateOffset.to, "day").toDate(),
        },
      ]
    : [{ after: dayjs(new Date(0, 0, 1)).toDate() }];

  return (
    <Panel
      styles={styles.panel}
      dataSet={{ media: ids.panel }}
      shadow={Platform.OS === "web"}
    >
      {!showSkipHireCollectionDate && (
        <Heading
          level={5}
          styles={styles.productDate}
          dataSet={{ media: ids.productDate }}
        >
          {translate(
            deliveryOptions
              ? "hwa.screens.productDetails.dateAndTimeslot"
              : showDeliveryText(categorySku)
              ? "common.preferredDeliveryDate"
              : "common.preferredCollectionDate",
          )}
        </Heading>
      )}

      <FlexGrid rowSpacing={null} cellSpacing="xsmall">
        <FlexGrid.Cell
          base="100%"
          medium={
            deliveryOptions || showSkipHireCollectionDate ? "50%" : "100%"
          }
          key="date"
        >
          <Field
            name="date"
            styles={{ marginTop: 0 }}
            errorStyles={screens.medium && styles.dateError}
            invertErrorColors
            errorBackground
          >
            {({ field }) => (
              <View style={{ alignItems: "center" }}>
                <View>
                  {!deliveryOptions && showSkipHireCollectionDate && (
                    <View
                      style={{
                        alignItems: "center",
                        flexDirection: "row",
                        marginBottom: theme.spacing.small,
                      }}
                    >
                      <Heading
                        color="brand"
                        styles={styles.productDate}
                        dataSet={{ media: ids.productDate }}
                        level={screens.xlarge ? 5 : 6}
                      >
                        {isExchange && translate("common.exchangeDate")}
                        {!isExchange && translate("common.deliveryDate")}
                      </Heading>
                      {isExchange && (
                        <Tooltip size="small" styles={{ marginLeft: 3 }}>
                          <View>
                            <Heading
                              color="brand"
                              styles={{
                                fontFamily: "ProximaBold",
                                textAlign: "left",
                                marginBottom: theme.spacing.xsmall,
                              }}
                              level={4}
                            >
                              {translate("common.exchangeDate")}
                            </Heading>
                            <Text fontSize="small">
                              {translate(
                                "hwa.screens.productDetails.exchangeDescription",
                              )}
                              {collectionDateOffset.to &&
                                ` (${translate(
                                  "hwa.screens.productDetails.collectionRangeDescription",
                                  {
                                    days: collectionDateOffset.to,
                                  },
                                )})`}
                            </Text>
                          </View>
                        </Tooltip>
                      )}
                    </View>
                  )}
                  <Input.Date
                    {...field}
                    dateFormat="YYYY-MM-DD"
                    disabledDays={[...blackouts, { before: serverDate }]}
                    multiMonth={
                      !showSkipHireCollectionDate &&
                      ((screens.xlarge && !deliveryOptions) ||
                        (screens.medium && !screens.large && !deliveryOptions))
                    }
                    firstDay={1}
                    onChange={(event) => {
                      field.onChange(event);
                      if (showSkipHireCollectionDate)
                        setValue("collectionDate", "");
                    }}
                    month={getFirstAvailableMonth(blackouts, serverDate)}
                  />
                </View>
              </View>
            )}
          </Field>
        </FlexGrid.Cell>
        {!deliveryOptions && showSkipHireCollectionDate && (
          <FlexGrid.Cell base="100%" medium="50%" key="collectionDate">
            <Field
              name="collectionDate"
              styles={{ marginTop: 0 }}
              errorStyles={screens.medium && styles.dateError}
              invertErrorColors
              errorBackground
            >
              {({ field }) => (
                <View
                  style={{
                    alignItems: "center",
                  }}
                >
                  <View>
                    {/* Tooltip */}
                    <View
                      style={{
                        alignItems: "center",
                        flexDirection: "row",
                        marginBottom: theme.spacing.small,
                      }}
                    >
                      <Heading
                        styles={styles.productDate}
                        dataSet={{ media: ids.productDate }}
                        level={screens.xlarge ? 5 : 6}
                      >
                        {translate("common.collectionReadyDate")}
                      </Heading>
                      <Tooltip
                        size="small"
                        color="brand.darker"
                        styles={{ marginLeft: 3 }}
                      >
                        <View>
                          <Heading
                            color="brand"
                            styles={{
                              fontFamily: "ProximaBold",
                              textAlign: "left",
                              marginBottom: theme.spacing.xsmall,
                            }}
                            level={4}
                          >
                            {translate("common.collectionReadyDate")}
                          </Heading>
                          <Text fontSize="small">
                            {translate(
                              "hwa.screens.productDetails.collectionReadyDateDescription",
                            )}
                          </Text>
                        </View>
                      </Tooltip>
                    </View>
                    <Input.Date
                      {...field}
                      key={`collectionDate-${dateValue}`}
                      color="warning"
                      dateFormat="YYYY-MM-DD"
                      disabledDays={collectionDateDisabledDays}
                      firstDay={1}
                      month={getFirstAvailableMonth(
                        [...collectionDateDisabledDays, ...blackouts],
                        new Date(dateValue || serverDate),
                      )}
                    />
                  </View>
                </View>
              )}
            </Field>
          </FlexGrid.Cell>
        )}
        {deliveryOptions && (
          <FlexGrid.Cell
            base="100%"
            medium="50%"
            style={{
              alignItems: "center",
              top: -theme.spacing.xsmall,
            }}
          >
            <Field name="delivery.deliveryOption">
              {({ field }) => (
                <Input.List
                  {...field}
                  optionsStyles={{
                    padding: 0,
                    borderBottomWidth: 0,
                  }}
                  onChange={(value) => {
                    onListChange(value);
                    field.onChange(value);
                  }}
                  options={deliveryOptions.map(
                    mapDelivery(
                      selectedOption,
                      translate,
                      deliveryOptions.length,
                    ),
                  )}
                  optionStyles={{
                    ...theme.fontDefaults.small,
                    padding: 0,
                  }}
                  customIcon={
                    <View
                      style={{
                        position: "absolute",
                        right: theme.spacing.medium,
                      }}
                    >
                      <Icon.Tick2
                        size="medium"
                        color={
                          selectedOption === deliveryOptions[0].title
                            ? "light"
                            : "accent.darker"
                        }
                      />
                    </View>
                  }
                />
              )}
            </Field>

            {rangeDeliveryOptions &&
              rangeDeliveryOptions.title === selectedOption && (
                <Field
                  name="delivery.timeslot"
                  styles={{ marginVertical: -theme.spacing.small }}
                  errorStyles={[screens.medium && styles.timeslotError]}
                  invertErrorColors
                  errorBackground
                >
                  {({ field }) => (
                    <Input.Select
                      {...field}
                      options={
                        rangeDeliveryOptions?.title === selectedOption
                          ? rangeDeliveryOptions.range.map(mapRangeDelivery)
                          : []
                      }
                      optionStyles={{
                        fontFamily: "ProximaSemiBold",
                        fontSize: theme.fontDefaults.xsmall.fontSize,
                      }}
                      placeholder={translate(
                        "hwa.screens.productDetails.chooseSlot",
                      )}
                      disabled={rangeDeliveryOptions?.title !== selectedOption}
                      usePortal
                    />
                  )}
                </Field>
              )}
          </FlexGrid.Cell>
        )}
      </FlexGrid>
    </Panel>
  );
};
